import { useState } from 'react';
import type { InstallView, ListingCard as ListingCardData } from '@dunea/shared';
import type { ListingSort } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CAPTION,
  CARD,
  LoadError,
  Section,
  Select,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { CATALOG_TABS, cardPriceLabel, categoryLabel, installFor, SORTS, type CatalogTab } from './marketplace-model';
import { CategoryChips, DeveloperLine, ListingBadges, ListingCard, ListingIcon } from './parts';
import { useListings } from './use-listings';

const EMPTY: Record<CatalogTab, string> = {
  plugins: 'No plugins yet. Check back soon.',
  templates: 'No templates yet. Check back soon.',
  skills: 'No skills yet. Check back soon.',
};

/** Search bar + sort, shared by the Plugins and Themes catalogs. */
export function CatalogControls({
  q,
  onQ,
  sort,
  onSort,
  placeholder,
}: {
  q: string;
  onQ: (next: string) => void;
  sort: ListingSort;
  onSort: (next: ListingSort) => void;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <div className="i-ph:magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" />
        <input
          type="search"
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="ui-input !py-2 pl-8"
        />
      </div>
      <Select value={sort} onChange={(v) => onSort(v as ListingSort)} label="Sort" className="w-[118px] shrink-0">
        {SORTS.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** A catalog (plugins, templates or skills): search, category chips, sort, a featured row and the card list. */
export function BrowseView({
  tab = 'plugins',
  installs,
  onOpen,
}: {
  tab?: CatalogTab;
  installs: InstallView[] | null;
  onOpen: (slug: string) => void;
}) {
  const config = CATALOG_TABS[tab];
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<ListingSort>('popular');
  const list = useListings(config.kind, { q, category, sort });
  const categoryOptions = config.categories.map((c) => [c, categoryLabel(c)] as const);

  const browsing = !q.trim() && !category;
  const featured = browsing ? list.items.filter((i) => i.featured) : [];

  return (
    <div className="space-y-4">
      <CatalogControls q={q} onQ={setQ} sort={sort} onSort={setSort} placeholder={`Search ${config.noun}`} />
      <CategoryChips
        categories={categoryOptions}
        value={category}
        onChange={setCategory}
        label={`${categoryLabel(config.noun)} categories`}
      />

      {list.error && !list.loading ? (
        <LoadError message={`Could not load ${config.noun}.`} onRetry={() => void list.reload()} />
      ) : list.loading && list.items.length === 0 ? (
        <div className="space-y-2.5">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <>
          {featured.length > 0 && (
            <Section title="Featured" icon="i-ph:sparkle">
              <div className="-mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1">
                {featured.map((item) => (
                  <FeaturedCard key={item.slug} item={item} onOpen={() => onOpen(item.slug)} />
                ))}
              </div>
            </Section>
          )}

          <Section
            title={browsing ? `All ${config.noun}` : 'Results'}
            icon="i-ph:squares-four"
            aside={
              <span className="flex items-center gap-1.5 text-[11px] text-muted">
                {(list.loading || list.settling) && <Spinner className="text-[11px]" />}
                {list.total > 0 && `${list.total}`}
              </span>
            }
          >
            {list.items.length === 0 ? (
              <div className={classNames(CARD, 'px-4 py-6 text-center text-[12px] text-muted')}>
                {browsing ? EMPTY[tab] : `No ${config.noun} match. Try another search or category.`}
              </div>
            ) : (
              <div className="space-y-2">
                {list.items.map((item) => (
                  <ListingCard
                    key={item.slug}
                    item={item}
                    onOpen={() => onOpen(item.slug)}
                    badge={tab !== 'templates' && installFor(installs, item.slug) ? 'Installed' : null}
                  />
                ))}
              </div>
            )}

            {list.hasMore && (
              <div className="flex flex-col items-center gap-1.5 pt-1">
                <button
                  type="button"
                  onClick={() => void list.loadMore()}
                  disabled={list.loadingMore}
                  className={BTN_SECONDARY}
                >
                  {list.loadingMore && <Spinner />}
                  Load more
                </button>
                {list.moreError && <span className="text-[11px] text-danger">Could not load more. Try again.</span>}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function FeaturedCard({ item, onOpen }: { item: ListingCardData; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={classNames(
        CARD,
        'flex w-[220px] shrink-0 snap-start flex-col gap-2.5 p-3.5 text-left transition-colors hover:border-subtle',
      )}
    >
      <div className="flex items-center gap-2.5">
        <ListingIcon url={item.iconUrl} name={item.name} size={32} />
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-primary">{item.name}</div>
          <DeveloperLine developer={item.developer} />
        </div>
      </div>
      <p className="line-clamp-2 text-[11.5px] leading-relaxed text-secondary">{item.tagline}</p>
      <div className="mt-auto flex flex-wrap items-center gap-1.5">
        <span className={CAPTION}>{cardPriceLabel(item)}</span>
        <ListingBadges item={{ badges: (item.badges ?? []).filter((b) => b !== 'featured'), featured: false }} />
      </div>
    </button>
  );
}
